import type { ReactNode } from 'react';
import { AnimatedNumber } from '@savvy-kit/foundations/motion/animated-number';
import { FadeIn } from '@savvy-kit/foundations/motion/chart-motion';
import { GainNumber, PercentDelta } from '@savvy-kit/foundations/motion/gain-number';
import { gainDelayFor } from '@savvy-kit/foundations/motion/grading';
import { DELAY_GAIN, DELAY_PERCENT, DUR_STAT, INTENSITY_SECONDARY } from '@savvy-kit/foundations/motion/tokens';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

import { changeDirection, EM_DASH, roundsAwayToZero, safeSignedMoney, safeSignedPercent } from './utils';

const DIRECTION_CLASS = {
  up: 'text-success',
  down: 'text-destructive',
  flat: 'text-secondary',
} as const;

/**
 * How the figure moves. `figure` is a card's own reading (the range movement, today's change): it dips, swaps
 * and springs back on the gain beat. `stat` is a supporting cell: it fades up and rolls on the supporting beat.
 * Omitted, the figure is static.
 */
export type ValueChangeMotion =
  | {
      readonly kind: 'figure';
      readonly trigger?: string | number | boolean | null;
      readonly delay?: number;
      /** Count each figure from here rather than settling in place; `capTravel: false` shows the whole climb. */
      readonly from?: { readonly amount?: number | null; readonly percent?: number | null };
      readonly capTravel?: boolean;
      /** The headline's from/to so the percentage delay can scale with the headline's travel distance. */
      readonly headlineFrom?: number | null;
      readonly headlineTo?: number | null;
    }
  | { readonly kind: 'stat'; readonly delay?: number };

type ValueChangeProps = {
  readonly amount?: number | null;
  readonly percent?: number | null;
  readonly caption?: string;
  readonly captionNote?: ReactNode;
  readonly motion?: ValueChangeMotion;
  readonly className?: string;
  readonly layout?: 'inline' | 'stacked';
};

function parenthesised(format: (value: number) => string): (value: number) => string {
  return (value: number) => `(${format(value)})`;
}

type FiguresProps = {
  readonly amount: number | null | undefined;
  readonly percent: number | null | undefined;
  readonly motion: ValueChangeMotion;
};

function MovingFigures({ amount, percent, motion }: FiguresProps) {
  const both = !isNil(amount) && !isNil(percent);
  const percentFormat = both ? parenthesised(safeSignedPercent) : safeSignedPercent;

  if (motion.kind === 'stat') {
    const delay = motion.delay ?? 0;
    return (
      <span aria-hidden className="inline-flex items-center gap-x-1">
        {!isNil(amount) && (
          <AnimatedNumber value={amount} format={safeSignedMoney} duration={DUR_STAT} delay={delay} labelled={false} />
        )}
        {!isNil(percent) && (
          <AnimatedNumber value={percent} format={percentFormat} duration={DUR_STAT} delay={delay} labelled={false} />
        )}
      </span>
    );
  }

  // A caller's `delay` is the gain beat plus whatever the page adds for its reveal; the percentage keeps that
  // addition and lands on its own beat after the headline, which waits longer the further it had to count.
  const offset = (motion.delay ?? DELAY_GAIN) - DELAY_GAIN;
  const percentDelay =
    offset +
    (!isNil(motion.headlineFrom) && !isNil(motion.headlineTo)
      ? gainDelayFor(motion.headlineFrom, motion.headlineTo)
      : DELAY_PERCENT);

  return (
    <span aria-hidden className="inline-flex items-center gap-x-1">
      {!isNil(amount) && (
        <GainNumber
          value={amount}
          from={motion.from?.amount ?? undefined}
          capTravel={motion.capTravel}
          trigger={motion.trigger}
          countThrough
          coins={false}
          intensity={INTENSITY_SECONDARY}
          delay={motion.delay ?? DELAY_GAIN}
          format={safeSignedMoney}
          labelled={false}
        />
      )}
      {!isNil(percent) &&
        (!isNil(motion.from?.percent) ? (
          // A percentage measured from a starting point — the legend's return since the range opened — is
          // the bench's percentage figure: it counts the whole way from there on every range switch.
          <PercentDelta
            value={percent}
            from={motion.from.percent}
            capTravel={motion.capTravel}
            trigger={motion.trigger}
            delay={percentDelay}
            format={percentFormat}
            labelled={false}
          />
        ) : (
          <AnimatedNumber
            key={String(motion.trigger)}
            value={percent}
            format={percentFormat}
            duration={DUR_STAT}
            delay={percentDelay}
            labelled={false}
          />
        ))}
    </span>
  );
}

type StackedLineProps = {
  readonly text: string;
  readonly amount?: number | null;
  readonly percent?: number | null;
  readonly motion: ValueChangeMotion | undefined;
};

function StackedLine({ text, amount, percent, motion }: StackedLineProps) {
  if (motion === undefined) {
    return text;
  }
  return (
    <>
      <span className="sr-only">{text}</span>
      <MovingFigures amount={amount} percent={percent} motion={motion} />
    </>
  );
}

export function ValueChange({
  amount,
  percent,
  caption,
  captionNote,
  motion,
  className,
  layout = 'inline',
}: ValueChangeProps) {
  const amountText = isNil(amount) ? null : safeSignedMoney(amount);
  const percentText = isNil(percent) ? null : safeSignedPercent(percent);
  const rendered = [amountText, percentText].filter((text) => text !== null);

  const captionGroup =
    isNil(caption) && isNil(captionNote) ? null : (
      <span data-slot="value-change-caption" className="flex items-center gap-1.5 text-secondary">
        {caption}
        {captionNote}
      </span>
    );

  if (rendered.length === 0) {
    return (
      <span className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-secondary', className)}>
        <span>{EM_DASH}</span>
        {captionGroup}
      </span>
    );
  }

  const basis = !isNil(amount) ? amount : percent;
  const showsNoMovement = rendered.every((text) => roundsAwayToZero(text));
  const direction = showsNoMovement ? 'flat' : changeDirection(basis ?? null);
  const Arrow = direction === 'down' ? ArrowDownRight : ArrowUpRight;

  if (layout === 'stacked' && amountText !== null && percentText !== null) {
    const stackedFigure = (
      <>
        {direction !== 'flat' && (
          <Arrow data-slot="value-change-arrow" size={14} className={cn('shrink-0', DIRECTION_CLASS[direction])} />
        )}
        <span className="flex flex-col">
          <span className={cn('font-medium tabular-nums', DIRECTION_CLASS[direction])}>
            <StackedLine text={amountText} amount={amount} motion={motion} />
          </span>
          <span className="flex items-center gap-1.5">
            <span className={cn('font-medium tabular-nums', DIRECTION_CLASS[direction])}>
              <StackedLine text={percentText} percent={percent} motion={motion} />
            </span>
            {captionGroup}
          </span>
        </span>
      </>
    );

    return (
      <span
        data-slot="value-change"
        data-direction={direction}
        data-layout="stacked"
        className={cn('flex items-center gap-x-1.5 text-sm', className)}
      >
        {motion?.kind === 'stat' ? (
          <FadeIn delay={motion.delay ?? 0} className="flex items-center gap-x-1.5">
            {stackedFigure}
          </FadeIn>
        ) : (
          stackedFigure
        )}
      </span>
    );
  }

  const text =
    amountText !== null && percentText !== null ? `${amountText} (${percentText})` : (amountText ?? percentText);

  const figure = (
    <>
      {direction !== 'flat' && (
        <Arrow data-slot="value-change-arrow" size={14} className={DIRECTION_CLASS[direction]} />
      )}
      <span className={cn('font-medium tabular-nums', DIRECTION_CLASS[direction])}>
        {motion === undefined ? (
          text
        ) : (
          <>
            <span className="sr-only">{text}</span>
            <MovingFigures amount={amount} percent={percent} motion={motion} />
          </>
        )}
      </span>
    </>
  );

  return (
    <span
      data-slot="value-change"
      data-direction={direction}
      className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm', className)}
    >
      {motion?.kind === 'stat' ? (
        <FadeIn delay={motion.delay ?? 0} className="inline-flex items-center gap-x-1.5">
          {figure}
        </FadeIn>
      ) : (
        figure
      )}
      {captionGroup}
    </span>
  );
}
